import { useState, useCallback } from "react";

/**
 * Custom hook for managing button loading states
 * Provides a clean way to handle async operations with loading indicators
 * 
 * @param {string} defaultText - The default button text (e.g., "Sign in")
 * @param {string} loadingText - The text to show while loading (e.g., "Signing in...")
 * @returns {Object} - { isLoading, buttonText, executeAsync }
 * 
 * @example
 * const { isLoading, buttonText, executeAsync } = useButtonLoading("Sign in", "Signing in...");
 * 
 * const handleSubmit = async (e) => {
 *   e.preventDefault();
 *   await executeAsync(async () => {
 *     await loginAPI(email, password);
 *   });
 * };
 * 
 * <Button loading={isLoading} disabled={isLoading}>
 *   {buttonText}
 * </Button>
 */
export const useButtonLoading = (defaultText = "Submit", loadingText = "Loading...") => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Execute an async function with automatic loading state management
   * @param {Function} asyncFn - The async function to execute
   * @param {Object} options - Optional configuration
   * @param {Function} options.onSuccess - Callback on success
   * @param {Function} options.onError - Callback on error
   * @param {Function} options.onFinally - Callback that always runs
   * @returns {Promise} - The result of the async function
   */
  const executeAsync = useCallback(async (asyncFn, options = {}) => {
    const { onSuccess, onError, onFinally } = options;
    
    setIsLoading(true);
    
    try {
      const result = await asyncFn();
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        // Re-throw if no error handler provided
        throw error;
      }
    } finally {
      if (onFinally) {
        onFinally();
      }
      // Only reset loading if we're not redirecting
      // (component might unmount before this runs)
      setIsLoading(false);
    }
  }, []);

  /**
   * Manually set loading state (for advanced use cases)
   */
  const setLoading = useCallback((loading) => {
    setIsLoading(loading);
  }, []);

  /**
   * Reset loading state
   */
  const reset = useCallback(() => {
    setIsLoading(false);
  }, []);

  const buttonText = isLoading ? loadingText : defaultText;

  return {
    isLoading,
    buttonText,
    executeAsync,
    setLoading,
    reset,
  };
};

export default useButtonLoading;
